import { DotOfferBlockFreeformEnum } from '@feature/types/offer-block.type';
import {
  Alignment,
  PromoBlockDataType,
  PromoBlockResolverResult,
  PromoBlockView,
  ViewPromoCard,
} from '@feature/types/promo-block.type';
import {
  JbFlagCornerPosition,
  JbFlagTheme,
  JbThemeEnum,
} from 'jb-component-library';

const getExternalButtonsForOneButton = oneButtonObj => {
  if (oneButtonObj[0].offer && oneButtonObj[1].button) {
    return oneButtonObj[1].button?.length === 1 ||
      oneButtonObj[1].button?.length !== oneButtonObj[0].offer?.length
      ? oneButtonObj[1].button
      : [];
  } else if (oneButtonObj[0].button) {
    return oneButtonObj[0].button;
  } else {
    return [];
  }
};

const sanitizeOfferBlockValue = offerBlock => {
  if (offerBlock.valueType !== DotOfferBlockFreeformEnum.freeform) {
    // Sanitize the value by removing non-numeric characters
    offerBlock.value = offerBlock.value.replace(/[^\d.-]/g, '');
  }
  return offerBlock;
};

// flattens some unnecessary arrays, and adds a property, 'externalButtons' in the case where there are an uneven
// amount of buttons and offers - which will require us to have our buttons live outside of our offer blocks, in the template
// If/Else clauses are used to define when there is 1, 2 or offer block buttons and to use 'externalButtons' the right time and
// the right spot
const remapPromoBlock = (promo: PromoBlockDataType): PromoBlockView => {
  let promoCard: ViewPromoCard;
  if (promo?.promoCard?.[0] && 'oneButton' in promo?.promoCard?.[0]) {
    promoCard = {
      ...promo.promoCard[0].oneButton[0],
      ...promo.promoCard[0].oneButton[1],
      externalButtons: getExternalButtonsForOneButton(
        promo.promoCard[0].oneButton,
      ),
    };
  } else if (promo?.promoCard?.[0] && 'twoButton' in promo?.promoCard?.[0]) {
    promoCard = {
      offer: promo.promoCard[0].twoButton
        .map(data => ({
          ...data.offer?.[0],
        }))
        .filter(offer => !!offer),
      button: promo.promoCard[0].twoButton
        .map(data => data.button?.[0])
        .filter(button => !!button),
    };

    promoCard.externalButtons =
      promoCard.offer.length !== promoCard.button.length
        ? promoCard.button
        : [];
  }

  // this block of code is more about cosmetic settings and to increase the readability in the HTMl
  const rootPromoData = promo;

  const sanitizedOfferBlocks = promoCard?.offer?.map(offerBlock =>
    offerBlock.value ? sanitizeOfferBlockValue(offerBlock) : offerBlock,
  );

  return {
    promoBlock: {
      ...rootPromoData,
      promoCard,
    },
    fullPromoBlockLink:
      promoCard?.button?.length === 1 ? promoCard?.button[0].href : null,
    flagSettings: {
      flagTitle: rootPromoData.flag?.[0].title,
      flagTheme:
        rootPromoData.theme === JbFlagTheme.light
          ? JbFlagTheme.dark
          : JbFlagTheme.light,
      flagAlignment:
        rootPromoData?.contentAlignment === Alignment.center
          ? JbFlagCornerPosition.bottom
          : JbFlagCornerPosition.bottomRight,
    },
    offerBlockTheme:
      rootPromoData.theme === JbThemeEnum.light
        ? JbThemeEnum.light
        : JbThemeEnum.dark,
    offerBlocks: sanitizedOfferBlocks,
  };
};

export const promoBlockResolver = (
  registry,
  data: { promo: PromoBlockDataType[] },
  template,
): PromoBlockResolverResult => {
  return {
    data: data.promo.map(pr => remapPromoBlock(pr)),
  };
};
